import { useContext, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button } from '@m/alchemy-ui/Button';
import { Dialog } from '@m/alchemy-ui/Dialog';
import { Tabs } from '@m/alchemy-ui/Tabs';
import { Toast } from '@m/alchemy-ui/Toast';
import { theme as alchemyDefaultTheme, ThemeProvider } from '@m/alchemy-ui/theme';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Formik } from 'formik';
import { CommunityContext, UserContext } from '../../../context';
import { ButtonRow } from '../../../common/components';
import { CREATE_USER_CHALLENGE, type CreateUserChallengeMutation } from '../../../graphql/mutations';
import { ChallengeArchived } from '../../../common/enums';
import { defaultChallenge, defaultChallengeVisibility, type IChallenge } from '../../../common/interfaces';
import { getToastMessages } from '../../../helpers';
import { ToastLevel } from '../../../common/enums';
import { DEFAULT_LANGUAGE } from '../../shared-constants';
import { UserChallengeDetails } from './UserChallengeDetails';
import { UserChallengeAccess } from './UserChallengeAccess';
import { userChallengeValidationSchema } from './user-challenge-validation-schema';
import {
  DialogContent,
  StyledButtonRow,
  StyledDialogBody,
  TabContainer,
  ToastBox,
  ToastWrapper
} from './UserChallenge.styled';

const TAB_DETAILS = 'DETAILS';
const TAB_ACCESS = 'ACCESS';

export interface UserCreatedChallengeDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly challengeId?: string;
}

interface AccessUser {
  user: { id: string };
  canModerate: boolean;
}

interface AccessGroup {
  usergroup: { id: string };
  canModerate: boolean;
}

interface FileItem {
  id: string;
  hash?: string | null;
  userId?: string | null;
  extension?: string;
  name?: string;
  url?: string | null;
}

export const UserCreatedChallengeDialog = ({ isOpen, onClose }: UserCreatedChallengeDialogProps) => {
  const intl = useIntl();
  const user = useContext(UserContext);
  const community = useContext(CommunityContext);
  const [activeTab, setActiveTab] = useState(TAB_DETAILS);
  const [messages, setMessages] = useState<ReturnType<typeof getToastMessages>>();
  const portalTargetRef = useRef(null);

  const [createUserChallenge, { loading: createLoading }] =
    useMutation<CreateUserChallengeMutation>(CREATE_USER_CHALLENGE);

  const isLoading = createLoading;

  const tabData = [
    {
      id: 'tab_catDetails',
      label: intl.formatMessage({ id: 'challengeDetails', defaultMessage: 'Details' }),
      value: TAB_DETAILS
    },
    {
      id: 'tab_catPermissions',
      label: intl.formatMessage({ id: 'challengeAccess', defaultMessage: 'Challenge Access' }),
      value: TAB_ACCESS
    }
  ];

  const prePopulatedAccess = useMemo(() => {
    const adminUsers = community.defaultAccess?.users ?? [];
    const adminGroups = community.defaultAccess?.groups ?? [];
    const isCreatorAlreadyAdmin = adminUsers.some((accessUser) => accessUser.user.id === user.id);
    const users = isCreatorAlreadyAdmin
      ? adminUsers
      : [
          ...adminUsers,
          {
            user: {
              id: user.id,
              displayName: user.displayName,
              screenName: user.screenName,
              createdAt: user.createdAt,
              firstName: user.firstName,
              lastName: user.lastName,
              profileImageId: user.profileImageId,
              timezone: user.timezone,
              profileImageFile: user.profileImageFile
            },
            canModerate: true
          }
        ];

    return {
      users,
      groups: adminGroups,
      options: {
        ...defaultChallenge.access?.options,
        ...defaultChallengeVisibility
      }
    };
  }, [community.defaultAccess, user]);

  const initialValues: IChallenge = useMemo(() => {
    return {
      ...defaultChallenge,
      language: DEFAULT_LANGUAGE,
      loading: false,
      isNew: true,
      archived: ChallengeArchived.PUBLISHED,
      access: prePopulatedAccess
    };
  }, [prePopulatedAccess]);

  const creatorUserId = user.id;
  const handleSave = async (values: IChallenge) => {
    try {
      const accessInput = values.access
        ? {
            users: (values.access.users || []).map(({ user: accessUser, canModerate }: AccessUser) => ({
              typeId: accessUser.id,
              canModerate
            })),
            groups: (values.access.groups || []).map(({ usergroup, canModerate }: AccessGroup) => ({
              typeId: usergroup.id,
              canModerate
            })),
            options: values.access.options
              ? {
                  comments: values.access.options.comments
                    ? {
                        enabled: values.access.options.comments.enabled,
                        attachmentsEnabled: values.access.options.comments.attachmentsEnabled,
                        anonymousEnabled: values.access.options.comments.anonymousEnabled
                      }
                    : undefined,
                  ideaSubmissionEnabled: values.access.options.ideaSubmissionEnabled,
                  ideasVisible: values.access.options.ideasVisible,
                  ideaSubmissionsType: values.access.options.ideaSubmissionsType,
                  showClosedEnabled: values.access.options.showClosedEnabled,
                  voteOnCommentsEnabled: values.access.options.voteOnCommentsEnabled,
                  voteOnIdeasEnabled: values.access.options.voteOnIdeasEnabled,
                  privateSubmissionEnabled: values.access.options.privateSubmissionEnabled,
                  ideaEditLock: values.access.options.ideaEditLock
                    ? {
                        enabled: values.access.options.ideaEditLock.enabled,
                        graceMinutes:
                          values.access.options.ideaEditLock.graceMinutes === undefined
                            ? undefined
                            : Number(values.access.options.ideaEditLock.graceMinutes)
                      }
                    : undefined,
                  shareFacebookEnabled: values.access.options.shareFacebookEnabled,
                  shareTwitterEnabled: values.access.options.shareTwitterEnabled,
                  shareLinkedInEnabled: values.access.options.shareLinkedInEnabled,
                  copyUrlEnabled: values.access.options.copyUrlEnabled
                }
              : undefined
          }
        : undefined;

      const translations = values.translations?.map(({ id, iso, value, objectId, objectType, field }) => ({
        id,
        iso,
        value,
        objectId,
        objectType,
        field
      }));

      const challengeStatus = values.archived ?? ChallengeArchived.UNPUBLISHED;
      const minimalInput = {
        title: values.title,
        subtitle: values.subtitle || undefined,
        description: values.description,
        archived: challengeStatus,
        files: values.files
          ? {
              header: values.files.header
                ? {
                    id: values.files.header.id,
                    hash: values.files.header.hash,
                    userId: values.files.header.userId ?? user.id
                  }
                : undefined,
              list: values.files.list
                ? {
                    id: values.files.list.id,
                    hash: values.files.list.hash,
                    userId: values.files.list.userId ?? user.id
                  }
                : undefined,
              attachments: values.files.attachments?.map((f: FileItem) => ({
                id: f.id,
                hash: f.hash ?? '',
                userId: f.userId ?? user.id
              }))
            }
          : undefined,
        access: accessInput,
        translations: translations?.length ? translations : undefined
      };

      const { data: createData } = await createUserChallenge({
        variables: { input: minimalInput }
      });

      if (!createData?.createUserChallenge) {
        throw new Error('Failed to create challenge');
      }

      const targetChallengeId = createData.createUserChallenge.id;

      const successMessage = intl.formatMessage({
        id: 'challengeCreatedSuccess',
        defaultMessage: 'Challenge created successfully'
      });

      setMessages(getToastMessages(ToastLevel.CONFIRM, successMessage));

      window.location.href =
        challengeStatus === ChallengeArchived.PUBLISHED
          ? `/category/${targetChallengeId}`
          : `/user/activity?goTab=myChallenges`;
    } catch (error) {
      const failMessage = intl.formatMessage({
        id: 'challengeCreatedFail',
        defaultMessage: 'Failed to create challenge'
      });

      setMessages(getToastMessages(ToastLevel.ERROR, failMessage));
    }
  };

  const handleClose = () => {
    setActiveTab(TAB_DETAILS);
    setMessages(undefined);
    onClose();
  };

  const dialogTitle = intl.formatMessage({ id: 'createAChallenge', defaultMessage: 'Create a Challenge' });

  const isDarkTheme = community.colors?.userNavBg?.toLowerCase() === 'dark';
  const lightTheme = useMemo(
    () => ({
      ...alchemyDefaultTheme,
      defaults: {
        ...alchemyDefaultTheme.defaults,
        colorScheme: 'alchemyLight' as const
      }
    }),
    []
  );

  const dialogContent = (
    <Dialog isOpen={isOpen} onClose={handleClose} hasCloseIcon size="extraLarge" headerContent={dialogTitle}>
      <StyledDialogBody>
        <Formik
          initialValues={initialValues}
          validationSchema={userChallengeValidationSchema}
          onSubmit={handleSave}
          enableReinitialize
        >
          {({ submitForm }) => (
            <Form>
              <DialogContent>
                <TabContainer>
                  <Tabs
                    tabs={tabData}
                    activeTabValue={activeTab}
                    onTabClick={(_event: React.SyntheticEvent, tab: { value: string }) => setActiveTab(tab.value)}
                  />
                </TabContainer>
                {activeTab === TAB_DETAILS && <UserChallengeDetails />}
                {activeTab === TAB_ACCESS && <UserChallengeAccess creatorUserId={creatorUserId} />}
              </DialogContent>
              <ToastWrapper>
                <ToastBox ref={portalTargetRef}>
                  {/* @ts-expect-error */}
                  <Toast messages={messages} portalTarget={portalTargetRef.current} />
                </ToastBox>
              </ToastWrapper>
              <StyledButtonRow>
                <ButtonRow>
                  <Button priority="tertiary" onClick={handleClose}>
                    <FormattedMessage id="cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button priority="primary" type="button" onClick={submitForm} isLoading={isLoading}>
                    <FormattedMessage id="save" defaultMessage="Save" />
                  </Button>
                </ButtonRow>
              </StyledButtonRow>
            </Form>
          )}
        </Formik>
      </StyledDialogBody>
    </Dialog>
  );

  return isDarkTheme ? <ThemeProvider theme={lightTheme}>{dialogContent}</ThemeProvider> : dialogContent;
};
